"use client"

import { useState, useEffect } from "react"
import { Calendar } from "@/components/ui/calendar"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { CalendarDays, Target, TrendingUp, Flame } from "lucide-react"
import {
  getUserMonthlyCheckinsAction,
  getUserCheckinStatsAction,
} from "@/lib/actions/checkin-actions"
import type { UserDailyCheckin } from "@/types/question"

interface PracticeCalendarProps {
  className?: string
}

export function PracticeCalendar({ className }: PracticeCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [checkins, setCheckins] = useState<UserDailyCheckin[]>([])
  const [stats, setStats] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)

  // 获取打卡记录
  const fetchCheckins = async (date: Date) => {
    try {
      const year = date.getFullYear()
      const month = date.getMonth() + 1

      const [checkinsResult, statsResult] = await Promise.all([
        getUserMonthlyCheckinsAction(year, month),
        getUserCheckinStatsAction(30),
      ])

      if (checkinsResult.success && checkinsResult.data) {
        setCheckins(checkinsResult.data)
      }

      if (statsResult.success && statsResult.data) {
        setStats(statsResult.data)
      }
    } catch (error) {
      console.error("获取打卡记录失败:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCheckins(currentMonth)
  }, [currentMonth])

  // 检查指定日期是否有打卡记录
  const hasCheckinOnDate = (date: Date): boolean => {
    return checkins.some(checkin => {
      const checkinDate = new Date(checkin.date)
      return (
        checkinDate.getFullYear() === date.getFullYear() &&
        checkinDate.getMonth() === date.getMonth() &&
        checkinDate.getDate() === date.getDate()
      )
    })
  }

  // 获取指定日期的打卡记录
  const getCheckinOnDate = (date: Date): UserDailyCheckin | undefined => {
    return checkins.find(checkin => {
      const checkinDate = new Date(checkin.date)
      return (
        checkinDate.getFullYear() === date.getFullYear() &&
        checkinDate.getMonth() === date.getMonth() &&
        checkinDate.getDate() === date.getDate()
      )
    })
  }

  // 自定义日期渲染
  const modifiers = {
    checked: (date: Date) => hasCheckinOnDate(date),
  }

  const modifiersStyles = {
    checked: {
      backgroundColor: "#10b981",
      color: "white",
      borderRadius: "50%",
    },
  }

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CalendarDays className="h-5 w-5 mr-2" />
            打卡日历
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center">
          <CalendarDays className="h-5 w-5 mr-2" />
          打卡日历
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* 日历组件 */}
        <div className="flex justify-center">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={date => date && setSelectedDate(date)}
            onMonthChange={setCurrentMonth}
            modifiers={modifiers}
            modifiersStyles={modifiersStyles}
            className="rounded-md border w-full"
          />
        </div>

        {/* 选中日期的打卡详情 */}
        {selectedDate && (
          <div className="space-y-3">
            <h4 className="font-medium text-sm text-gray-700">
              {selectedDate.toLocaleDateString("zh-CN", {
                year: "numeric",
                month: "long",
                day: "numeric",
                weekday: "long",
              })}
            </h4>

            {(() => {
              const checkin = getCheckinOnDate(selectedDate)
              if (checkin) {
                return (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-green-800 font-medium text-sm">已完成学习</span>
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        ✓ 已打卡
                      </Badge>
                    </div>
                    <div className="text-xs text-green-700">恭喜！您在今天完成了学习任务。</div>
                  </div>
                )
              } else {
                return (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <span className="text-gray-600 text-sm">当天未进行学习</span>
                  </div>
                )
              }
            })()}
          </div>
        )}

        {/* 统计信息 */}
        {stats && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <Target className="h-4 w-4 text-blue-600" />
                <span className="text-lg font-bold text-blue-800">{stats.completedDays}</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">本月打卡</p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <Flame className="h-4 w-4 text-orange-600" />
                <span className="text-lg font-bold text-orange-800">{stats.currentStreak}</span>
              </div>
              <p className="text-xs text-orange-600 mt-1">连续天数</p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <TrendingUp className="h-4 w-4 text-green-600" />
                <span className="text-lg font-bold text-green-800">
                  {stats.totalDays > 0
                    ? ((stats.completedDays / stats.totalDays) * 100).toFixed(1)
                    : 0}
                  %
                </span>
              </div>
              <p className="text-xs text-green-600 mt-1">打卡率</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <CalendarDays className="h-4 w-4 text-purple-600" />
                <span className="text-lg font-bold text-purple-800">{stats.longestStreak}</span>
              </div>
              <p className="text-xs text-purple-600 mt-1">最长连击</p>
            </div>
          </div>
        )}

        {/* 图例 */}
        <div className="flex items-center justify-center space-x-4 text-xs text-gray-600">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span>已学习</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
            <span>未学习</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
